import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import {
  getAssistantConfig,
  getAssistantById,
  updateAssistant,
} from "../../actions/assistants";
import { RobotIcon } from "../Auth/onboarding/icons";
import BrandAppearanceCard from "./BrandAppearanceCard";
import TypographyCard from "./TypographyCard";
import LauncherIconCard from "./LauncherIconCard";
import WidgetLayoutCard from "./WidgetLayoutCard";
import BehaviorDisplayCard from "./BehaviorDisplayCard";
import PreviewPanel from "./PreviewPanel";
import ActionBar from "./ActionBar";
import { configToSettings, settingsToPayload } from "./mapConfig";
import { defaultSettings } from "./defaultSettings";
import LoadingState from "../../Components/LoadingState";

export default function Customization() {
  // Set by the sidebar switcher; the page customizes whichever is active.
  const assistantId = useSelector((state) => state.assistants.activeId);
  const dispatch = useDispatch();

  const [settings, setSettings] = useState(defaultSettings);
  // GET /assistants/:id — the config payload has no sourceUrl, and the sidebar
  // list is not guaranteed to have loaded, so the record is read directly.
  const [siteUrl, setSiteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const previewRef = useRef(null);

  // The config as last fetched: Reset returns here, and Save spreads it back in
  // so keys these cards don't edit aren't dropped.
  const loadedConfig = useRef(null);
  const savedTimeout = useRef(null);

  useEffect(() => () => clearTimeout(savedTimeout.current), []);

  const load = useCallback(async () => {
    if (!assistantId) return;

    setLoading(true);
    setError("");

    // The widget config drives the cards; the assistant record supplies the
    // sourceUrl the preview screenshots. They're independent, so fetch both.
    const [result, record] = await Promise.all([
      dispatch(getAssistantConfig(assistantId)),
      dispatch(getAssistantById(assistantId)),
    ]);

    setLoading(false);

    if (record?.success) setSiteUrl(record.assistant?.sourceUrl || "");

    if (!result?.success) {
      setError(result?.message || "We couldn't load your widget settings.");
      return;
    }

    loadedConfig.current = result.config;
    setSettings(configToSettings(result.config, defaultSettings));
  }, [assistantId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Back to what the server last gave us, not to the hardcoded defaults —
  // otherwise Reset would silently discard the saved branding.
  const handleReset = () => {
    setError("");
    setSettings(
      loadedConfig.current
        ? configToSettings(loadedConfig.current, defaultSettings)
        : defaultSettings,
    );
  };

  const handlePreview = () => {
    previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSave = async () => {
    if (!assistantId || saving) return;

    // The name heads the widget on the customer's site, so saving an empty
    // one would leave the assistant nameless there. Said out loud rather than
    // by disabling Save, which would leave the reason to be guessed at.
    if (!settings.name?.trim()) {
      setError("Give your assistant a name before saving.");
      return;
    }

    setSaving(true);
    setError("");

    const result = await dispatch(
      updateAssistant(
        assistantId,
        settingsToPayload(settings),
      ),
    );

    setSaving(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't save your changes.");
      return;
    }

    // Re-read so the next Save and Reset build on what the server actually
    // stored rather than on what we hoped it stored.
    await load();

    setSaved(true);
    savedTimeout.current = setTimeout(() => setSaved(false), 1500);
  };

  const renderBody = () => {
    if (!assistantId) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No assistant selected</strong>
          <p>Pick one from the switcher at the top of the sidebar.</p>
        </div>
      );
    }

    if (loading && !loadedConfig.current) {
      return (
        <LoadingState
          title="Loading your widget settings"
          detail="Reading how this assistant is set up."
        />
      );
    }

    return (
      <div className="cz-grid">
        <div className="cz-form-col">
          {error && <p className="auth-error">{error}</p>}

          <BrandAppearanceCard settings={settings} onChange={handleChange} />
          <TypographyCard settings={settings} onChange={handleChange} />
          <LauncherIconCard settings={settings} onChange={handleChange} />
          <WidgetLayoutCard settings={settings} onChange={handleChange} />
          <BehaviorDisplayCard settings={settings} onChange={handleChange} />
        </div>

        <div className="cz-preview-pane" ref={previewRef}>
          <PreviewPanel
            settings={settings}
            siteUrl={siteUrl}
            onChange={handleChange}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar
          title="Customization"
          subtitle="Make your assistant match your brand and website experience."
        />

        {/* cz-content hands the scrolling to the form column so the
            preview can stay put beside it. */}
        <div className="dash-content">{renderBody()}</div>

        {assistantId && (
          <ActionBar
            onReset={handleReset}
            onPreview={handlePreview}
            onSave={handleSave}
            saved={saved}
            saving={saving}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
}
